import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ApiError, apiFetch } from '../../lib/api';
import { getCurrentWarehouseId } from '../../lib/warehouse';
import { LocationTreeSelect } from '../location/location-tree-select';
import type { ProductDetail, ProductListItem } from '../product/types';
import { ExpiryBadge } from './expiry-badge';
import { NegativeOverrideModal } from './negative-override-modal';
import { resolveQuantityConversion, type ActiveUnit } from './quantity-conversion';
import type { FefoCandidateItem, MovementReasonItem, OutboundResponse } from './types';

export interface OutboundLine {
  readonly key: string;
  productId: string | null;
  productName: string;
  qty: string;
  unitId: string;
  locationId: string | null;
  batchId: string | null;
  /** 选中的批次是否偏离了 FefoService 权威候选集的顶位（M6-M3） */
  fefoOverride: boolean;
}

const emptyLine = (): OutboundLine => ({
  key: crypto.randomUUID(),
  productId: null,
  productName: '',
  qty: '',
  unitId: '',
  locationId: null,
  batchId: null,
  fefoOverride: false,
});

/**
 * 出库请求体构造——Quick 与 Document 共用同一个函数，不看 `mode`（唯一区别
 * 是调用方给它多少行）：这是"两种模式对同一逻辑输入产生同一请求体形状"这条
 * 不变量的字面体现，抽成独立函数导出是为了让这条不变量本身可以被直接测试
 * （M6-M6），不需要通过完整渲染两套 UI 交互流程去间接验证。
 */
export function buildOutboundBody(params: {
  readonly lines: readonly OutboundLine[];
  readonly reasonId: string;
  readonly reasonText: string;
  readonly requiresReasonText: boolean;
  readonly fefoOverrideReason: string;
}): Record<string, unknown> {
  const anyFefoOverride = params.lines.some((l) => l.fefoOverride);
  return {
    lines: params.lines.map((l) => ({
      batchId: l.batchId,
      productId: l.productId,
      quantity: { value: l.qty.trim(), unitId: l.unitId },
      locationId: l.locationId,
    })),
    reasonId: params.reasonId,
    ...(params.requiresReasonText ? { reasonText: params.reasonText.trim() } : {}),
    ...(anyFefoOverride && params.fefoOverrideReason.trim()
      ? { fefoOverrideReason: params.fefoOverrideReason.trim() }
      : {}),
  };
}

/**
 * 出库抽屉（Quick / Document 双模式，Spec §19、UX F3、退出标准 5）。两种模式
 * 共用同一个后端端点 `POST /inventory/outbound`、同一个请求体形状——差异
 * 完全在前端交互：Quick 单行、静默按 FEFO 顶位候选出库；Document 多行、
 * 每行显式挑选批次（可偏离 FEFO 顶位，后端按 inventory.override.fefo 判定，
 * 这里只做提示，真正的强制永远在后端）。绝不存在第二条后端写路径。
 */
export function OutboundDrawer({ onClose }: { readonly onClose: () => void }): React.JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const warehouseId = getCurrentWarehouseId()!;
  const [mode, setMode] = useState<'quick' | 'document'>('quick');
  const [lines, setLines] = useState<OutboundLine[]>([emptyLine()]);
  const [reasonId, setReasonId] = useState('');
  const [reasonText, setReasonText] = useState('');
  const [fefoOverrideReason, setFefoOverrideReason] = useState('');
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());
  const [success, setSuccess] = useState<OutboundResponse | null>(null);
  const [pendingOverrideBody, setPendingOverrideBody] = useState<Record<string, unknown> | null>(
    null,
  );

  const { data: reasonData } = useQuery({
    queryKey: ['inventory', 'movement-reasons'],
    queryFn: () => apiFetch<{ items: MovementReasonItem[] }>('/inventory/movement-reasons'),
  });
  // 出库表单只列 OUTBOUND / DAMAGED / EXPIRED 三类——ADJUSTMENT 属于库存盘点
  // 更正流程（M7 范围），不该出现在出库原因选择器里，即使数据库里同一张表。
  const reasons = (reasonData?.items ?? []).filter((r) =>
    r.appliesTo.some((a) => a === 'OUTBOUND' || a === 'DAMAGED' || a === 'EXPIRED'),
  );
  const selectedReason = reasons.find((r) => r.id === reasonId);
  // M6-M3：DAMAGED/EXPIRED 处置流程需要看到已过期批次——与 OutboundService
  // 实际构造的 MovementKind（reason.appliesTo[0]）保持同一判据。
  const allowExpired =
    selectedReason?.appliesTo[0] === 'DAMAGED' || selectedReason?.appliesTo[0] === 'EXPIRED';
  const anyFefoOverride = lines.some((l) => l.fefoOverride);

  const updateLine = (key: string, patch: Partial<OutboundLine>): void =>
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  const submit = async (body: Record<string, unknown>): Promise<OutboundResponse> => {
    try {
      return await apiFetch<OutboundResponse>('/inventory/outbound', {
        method: 'POST',
        headers: { 'idempotency-key': idempotencyKey },
        body,
      });
    } catch (err) {
      if (err instanceof ApiError && err.code === 'INVENTORY.NEGATIVE_STOCK_REQUIRES_OVERRIDE') {
        setPendingOverrideBody({ ...body, confirmNegativeOverride: true });
      }
      throw err;
    }
  };

  const buildBody = (): Record<string, unknown> =>
    buildOutboundBody({
      lines,
      reasonId,
      reasonText,
      requiresReasonText: selectedReason?.requiresText ?? false,
      fefoOverrideReason,
    });

  const mutation = useMutation({
    mutationFn: () => submit(buildBody()),
    onSuccess: (result) => {
      setSuccess(result);
      setPendingOverrideBody(null);
      void queryClient.invalidateQueries({ queryKey: ['inventory'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });

  const overrideMutation = useMutation({
    mutationFn: (elevationToken: string) =>
      apiFetch<OutboundResponse>('/inventory/outbound', {
        method: 'POST',
        headers: { 'idempotency-key': idempotencyKey, 'x-elevation-token': elevationToken },
        body: pendingOverrideBody,
      }),
    onSuccess: (result) => {
      setSuccess(result);
      setPendingOverrideBody(null);
      void queryClient.invalidateQueries({ queryKey: ['inventory'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });

  const linesValid =
    lines.length > 0 &&
    lines.every((l) => l.productId && l.qty.trim() && l.unitId && l.locationId && l.batchId);
  const reasonValid =
    reasonId !== '' && (!selectedReason?.requiresText || reasonText.trim() !== '');
  const canSubmit = linesValid && reasonValid && !mutation.isPending;

  const receiveAnother = (): void => {
    setSuccess(null);
    setIdempotencyKey(crypto.randomUUID());
    setLines([emptyLine()]);
    setReasonText('');
    setFefoOverrideReason('');
    mutation.reset();
  };

  const error = mutation.error ?? overrideMutation.error;

  return (
    <div className="drawer-overlay" onClick={onClose}>
      <aside className="drawer" onClick={(e) => e.stopPropagation()}>
        <div className="drawer__header">
          <h2 className="drawer__title">{t('outbound.title')}</h2>
          <button className="btn btn--ghost btn--sm" onClick={onClose}>
            ✕
          </button>
        </div>

        {success ? (
          <div style={{ padding: 'var(--space-4)' }}>
            <p className="badge badge--success" style={{ marginBottom: 'var(--space-3)' }}>
              {t('outbound.successTitle')}
            </p>
            {success.docNo && (
              <p>
                {t('outbound.successDetail', { doc: success.docNo, count: success.lines.length })}
              </p>
            )}
            <div className="row" style={{ gap: 'var(--space-2)', marginTop: 'var(--space-4)' }}>
              <button className="btn btn--primary" onClick={receiveAnother}>
                {t('outbound.another')}
              </button>
              <button className="btn btn--ghost" onClick={onClose}>
                {t('app.close')}
              </button>
            </div>
          </div>
        ) : (
          <div style={{ padding: 'var(--space-4)', overflowY: 'auto' }}>
            <div className="row" style={{ gap: 'var(--space-2)', marginBottom: 'var(--space-3)' }}>
              <button
                type="button"
                className={mode === 'quick' ? 'btn btn--primary btn--sm' : 'btn btn--ghost btn--sm'}
                onClick={() => {
                  setMode('quick');
                  setLines((ls) => ls.slice(0, 1));
                }}
              >
                {t('outbound.modeQuick')}
              </button>
              <button
                type="button"
                className={
                  mode === 'document' ? 'btn btn--primary btn--sm' : 'btn btn--ghost btn--sm'
                }
                onClick={() => setMode('document')}
              >
                {t('outbound.modeDocument')}
              </button>
            </div>

            {lines.map((line, i) => (
              <OutboundLineEditor
                key={line.key}
                line={line}
                index={i}
                mode={mode}
                warehouseId={warehouseId}
                allowExpired={allowExpired}
                onChange={(patch) => updateLine(line.key, patch)}
                onRemove={
                  mode === 'document' && lines.length > 1
                    ? () => setLines((ls) => ls.filter((l) => l.key !== line.key))
                    : undefined
                }
              />
            ))}

            {mode === 'document' && (
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={() => setLines((ls) => [...ls, emptyLine()])}
                style={{ marginTop: 'var(--space-2)' }}
              >
                {t('outbound.addLine')}
              </button>
            )}

            <label className="field" style={{ marginTop: 'var(--space-3)' }}>
              <span>{t('outbound.reason')}</span>
              <select value={reasonId} onChange={(e) => setReasonId(e.target.value)}>
                <option value="">{t('outbound.pickReason')}</option>
                {reasons.map((r) => (
                  <option key={r.id} value={r.id}>
                    {t(r.label)}
                  </option>
                ))}
              </select>
            </label>
            {selectedReason?.requiresText && (
              <label className="field">
                <span>{t('outbound.reasonText')}</span>
                <input value={reasonText} onChange={(e) => setReasonText(e.target.value)} />
              </label>
            )}

            {anyFefoOverride && (
              <label className="field">
                <span>{t('outbound.fefoOverrideReasonLabel')}</span>
                <input
                  value={fefoOverrideReason}
                  onChange={(e) => setFefoOverrideReason(e.target.value)}
                  placeholder={t('outbound.fefoOverrideReasonPlaceholder')}
                />
                <span className="field-hint">{t('outbound.fefoOverrideReasonHint')}</span>
              </label>
            )}

            {error instanceof ApiError && (
              <p className="field-error">
                {t(`errors:${error.code}`, { defaultValue: error.code, ...error.params })}
              </p>
            )}

            <div className="row row--between" style={{ marginTop: 'var(--space-4)' }}>
              <span />
              <div className="row" style={{ gap: 'var(--space-2)' }}>
                <button className="btn btn--ghost" onClick={onClose}>
                  {t('app.cancel')}
                </button>
                <button
                  className="btn btn--primary"
                  disabled={!canSubmit}
                  onClick={() => mutation.mutate()}
                >
                  {mutation.isPending ? t('app.loading') : t('outbound.confirm')}
                </button>
              </div>
            </div>
          </div>
        )}

        {pendingOverrideBody && (
          <NegativeOverrideModal
            intendedRequestBody={pendingOverrideBody}
            onCancel={() => setPendingOverrideBody(null)}
            onGranted={(token) => overrideMutation.mutate(token)}
          />
        )}
      </aside>
    </div>
  );
}

/** 单行编辑：产品 → 数量/单位 → 位置 → 批次（Quick 静默取 FEFO 顶位，Document 显式挑选） */
function OutboundLineEditor({
  line,
  index,
  mode,
  warehouseId,
  allowExpired,
  onChange,
  onRemove,
}: {
  readonly line: OutboundLine;
  readonly index: number;
  readonly mode: 'quick' | 'document';
  readonly warehouseId: string;
  readonly allowExpired: boolean;
  readonly onChange: (patch: Partial<OutboundLine>) => void;
  readonly onRemove?: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [productSearch, setProductSearch] = useState('');

  const { data: productOptions } = useQuery({
    queryKey: ['outbound', 'product-search', productSearch],
    queryFn: () =>
      apiFetch<{ items: ProductListItem[] }>(
        `/products?limit=8&query=${encodeURIComponent(productSearch)}`,
      ),
    enabled: productSearch.trim().length > 0 && line.productId === null,
  });
  const { data: product } = useQuery({
    queryKey: ['outbound', 'product', line.productId],
    queryFn: () => apiFetch<ProductDetail>(`/products/${line.productId}`),
    enabled: line.productId !== null,
  });
  const activeUnits: ActiveUnit[] = useMemo(
    () => (product?.units ?? []).filter((u) => !u.archivedAt),
    [product],
  );
  const selectedUnit = activeUnits.find((u) => u.uomId === line.unitId);
  const conversion = useMemo(
    () => resolveQuantityConversion(line.qty, selectedUnit, activeUnits),
    [line.qty, selectedUnit, activeUnits],
  );

  // M6-M3 整改：唯一权威的 FEFO 排序来源是后端 FefoService.candidates()——
  // 不再在前端用某个位置的库存列表重新排序近似逼近它。这个候选集是**整仓库
  // 范围**的（与 OutboundService.isTopCandidate() 判定 override 时依据的
  // 集合完全一致），按 (locationId) 过滤只用来知道"这些候选里哪些在当前
  // 选中的位置有货"，过滤后仍保留服务端给定的顺序——不能重新排序，否则
  // 又变回了本地近似。
  const { data: fefoData } = useQuery({
    queryKey: ['inventory', 'fefo-candidates', line.productId, allowExpired],
    queryFn: () =>
      apiFetch<{ items: FefoCandidateItem[] }>(
        `/inventory/fefo-candidates?productId=${line.productId}&allowExpired=${allowExpired}`,
      ),
    enabled: line.productId !== null,
  });
  const warehouseCandidates = fefoData?.items ?? [];
  const warehouseTopBatchId = warehouseCandidates[0]?.batchId ?? null;
  const localCandidates = useMemo(() => {
    if (!line.locationId) return [];
    return warehouseCandidates
      .map((c) => ({
        ...c,
        qtyHere: c.distribution.find((d) => d.locationId === line.locationId)?.qtyOnHandBase,
      }))
      .filter((c) => c.qtyHere !== undefined && c.qtyHere !== '0');
  }, [warehouseCandidates, line.locationId]);

  const pickProduct = (p: ProductListItem): void => {
    onChange({
      productId: p.id,
      productName: p.name,
      unitId: '',
      batchId: null,
      fefoOverride: false,
    });
    setProductSearch('');
  };

  return (
    <div className="card" style={{ padding: 'var(--space-3)', marginBottom: 'var(--space-3)' }}>
      {mode === 'document' && (
        <div className="row row--between">
          <strong>{t('outbound.lineLabel', { index: index + 1 })}</strong>
          {onRemove && (
            <button type="button" className="btn btn--ghost btn--sm" onClick={onRemove}>
              {t('app.cancel')}
            </button>
          )}
        </div>
      )}

      <label className="field">
        <span>{t('inbound.stepProduct')}</span>
        {line.productId && product ? (
          <div className="row" style={{ gap: 'var(--space-2)', alignItems: 'center' }}>
            <strong>{product.name}</strong>
            <span className="field-hint">{product.sku}</span>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() =>
                onChange({
                  productId: null,
                  productName: '',
                  unitId: '',
                  batchId: null,
                  fefoOverride: false,
                })
              }
            >
              {t('inbound.changeProduct')}
            </button>
          </div>
        ) : (
          <>
            <input
              placeholder={t('products.searchPlaceholder')}
              value={productSearch}
              onChange={(e) => setProductSearch(e.target.value)}
            />
            {(productOptions?.items ?? []).map((p) => (
              <button key={p.id} type="button" className="tree__row" onClick={() => pickProduct(p)}>
                <span style={{ flex: 2 }}>{p.name}</span>
                <span className="field-hint">{p.sku}</span>
              </button>
            ))}
          </>
        )}
      </label>

      {product && (
        <>
          <div className="row" style={{ gap: 'var(--space-2)', alignItems: 'flex-end' }}>
            <label className="field" style={{ maxWidth: '140px' }}>
              <span>{t('inbound.stepQuantity')}</span>
              <input
                inputMode="decimal"
                value={line.qty}
                onChange={(e) => onChange({ qty: e.target.value })}
                aria-invalid={conversion?.error != null}
              />
            </label>
            <label className="field" style={{ maxWidth: '160px' }}>
              <span>{t('units.unit')}</span>
              <select value={line.unitId} onChange={(e) => onChange({ unitId: e.target.value })}>
                <option value="">{t('inbound.pickUnit')}</option>
                {activeUnits.map((u) => (
                  <option key={u.uomId} value={u.uomId}>
                    {u.uomCode}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {conversion?.error && (
            <span className="field-error">
              {t(`errors:${conversion.error}`, {
                defaultValue: conversion.error,
                ...conversion.errorParams,
              })}
            </span>
          )}

          <label className="field">
            <span>{t('outbound.sourceLocation')}</span>
            <LocationTreeSelect
              warehouseId={warehouseId}
              value={line.locationId}
              onChange={(id) => onChange({ locationId: id, batchId: null, fefoOverride: false })}
              allowRoot={false}
            />
          </label>

          {line.locationId && (
            <div className="field">
              <span>{t('outbound.batch')}</span>
              {localCandidates.length === 0 ? (
                <p className="field-hint">{t('outbound.noBatchesHere')}</p>
              ) : mode === 'quick' ? (
                // Quick：静默取"这个位置能给出的、服务端权威顺序里最靠前"的那个候选，
                // 不展示挑选 UI——一旦候选集变化就重新对齐
                <QuickBatchAutoSelect
                  candidates={localCandidates}
                  warehouseTopBatchId={warehouseTopBatchId}
                  value={line.batchId}
                  onChange={(id, isOverride) => onChange({ batchId: id, fefoOverride: isOverride })}
                />
              ) : (
                localCandidates.map((c) => (
                  <FefoCandidateRow
                    key={c.batchId}
                    candidate={c}
                    checked={line.batchId === c.batchId}
                    isTop={c.batchId === warehouseTopBatchId}
                    onSelect={() =>
                      onChange({
                        batchId: c.batchId,
                        fefoOverride: c.batchId !== warehouseTopBatchId,
                      })
                    }
                  />
                ))
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * 副作用局限在这一个小组件里：候选集变化时把 batchId 静默对齐到"这个位置能给
 * 的、服务端权威顺序里最靠前"的那个。这个顶位只在与整仓库顶位（warehouseTopBatchId）
 * 一致时才不算 override——Quick 模式天生只看单个位置，仓库范围内更早到期的
 * 批次若在别的位置，这里必然选不到，如实上报 fefoOverride，而不是假装没这回事。
 */
/**
 * Document 模式单个候选批次行——从 map() 内联 JSX 抽出来是为了让"效期状态
 * 确实渲染出来了"（独立复审 NEW-H1：候选行必须让操作员看得到过期状态，
 * 不能悄悄推荐一个已过期批次）可以直接对这一个组件断言，不需要走完整的
 * 产品搜索/选位置交互链路。
 */
export function FefoCandidateRow({
  candidate,
  checked,
  isTop,
  onSelect,
}: {
  readonly candidate: FefoCandidateItem & { readonly qtyHere?: string };
  readonly checked: boolean;
  readonly isTop: boolean;
  readonly onSelect: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <label className="row" style={{ gap: 'var(--space-2)' }}>
      <input type="radio" checked={checked} onChange={onSelect} />
      <span>{candidate.batchNo}</span>
      <span className="field-hint">{t('inbound.candidateQty', { qty: candidate.qtyHere })}</span>
      <ExpiryBadge
        expirationDate={candidate.expirationDate}
        hasExpiration={candidate.expirationDate !== null}
      />
      {isTop && <span className="badge badge--info">{t('outbound.fefoRecommended')}</span>}
    </label>
  );
}

export function QuickBatchAutoSelect({
  candidates,
  warehouseTopBatchId,
  value,
  onChange,
}: {
  readonly candidates: ReadonlyArray<FefoCandidateItem & { readonly qtyHere?: string }>;
  readonly warehouseTopBatchId: string | null;
  readonly value: string | null;
  readonly onChange: (id: string, isOverride: boolean) => void;
}): React.JSX.Element {
  const top = candidates[0];
  const isOverride = top !== undefined && top.batchId !== warehouseTopBatchId;
  useEffect(() => {
    if (top && value !== top.batchId) onChange(top.batchId, isOverride);
  }, [top, value, isOverride, onChange]);
  return top ? (
    <div className="row" style={{ gap: 'var(--space-2)', alignItems: 'center' }}>
      <span className="field-hint">
        {top.batchNo} · {top.qtyHere}
      </span>
      <ExpiryBadge
        expirationDate={top.expirationDate}
        hasExpiration={top.expirationDate !== null}
      />
    </div>
  ) : (
    <></>
  );
}
